
import React from 'react';
import { useTranslationUtils } from '@/hooks/useTranslationUtils';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Edit,
  Trash,
  Award,
  Users,
  CalendarClock
} from 'lucide-react';
import { formatDistanceToNow, isPast, format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Sweepstakes } from '@/hooks/useSweepstakesData';

interface SweepstakesTableProps {
  sweepstakes: Sweepstakes[];
  loading: boolean;
  handleEdit: (sweepstake: Sweepstakes) => void;
  handleDelete: (id: string) => void;
  runManualDraw: (id: string) => void;
  fetchEntries: (id: string) => void;
}

const SweepstakesTable: React.FC<SweepstakesTableProps> = ({
  sweepstakes,
  loading,
  handleEdit,
  handleDelete,
  runManualDraw,
  fetchEntries
}) => {
  const { t } = useTranslationUtils();
  
  const getStatusBadge = (status: string, drawDate: string) => {
    switch(status) {
      case 'scheduled':
        return isPast(new Date(drawDate))
          ? <Badge variant="destructive">{t('admin.overdueDrawing', 'Overdue')}</Badge>
          : <Badge variant="outline">{t('admin.scheduled', 'Scheduled')}</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-green-100 text-green-800 hover:bg-green-200">{t('admin.completed', 'Completed')}</Badge>;
      case 'cancelled':
        return <Badge variant="secondary">{t('admin.cancelled', 'Cancelled')}</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (sweepstakes.length === 0) {
    return (
      <div className="text-center p-8 border rounded-md">
        <p className="text-muted-foreground">{t('admin.noSweepstakes', 'No sweepstakes found')}</p>
      </div>
    );
  }

  return (
    <div className="border rounded-md overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('admin.sweepstakesName', 'Name')}</TableHead>
            <TableHead>{t('admin.dates', 'Dates')}</TableHead>
            <TableHead>{t('admin.status', 'Status')}</TableHead>
            <TableHead>{t('admin.winner', 'Winner')}</TableHead>
            <TableHead className="text-right">{t('admin.actions', 'Actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sweepstakes.map((item) => (
            <TableRow key={item.id}>
              <TableCell className="font-medium">
                {item.name}
                {item.description && (
                  <p className="text-xs text-muted-foreground mt-1 truncate max-w-md">
                    {item.description}
                  </p>
                )}
              </TableCell>
              <TableCell className="text-sm">
                <div className="flex items-center space-x-1 text-muted-foreground">
                  <CalendarClock className="h-3.5 w-3.5 mr-1" />
                  <span>{t('admin.start')}: {format(new Date(item.start_date), 'MMM d, yyyy')}</span>
                </div>
                <div className="flex items-center space-x-1 mt-1">
                  <Award className="h-3.5 w-3.5 mr-1" />
                  <span>{t('admin.draw')}: {format(new Date(item.draw_date), 'MMM d, yyyy')}</span>
                </div>
              </TableCell>
              <TableCell>
                {getStatusBadge(item.status, item.draw_date)}
              </TableCell>
              <TableCell>
                {item.winner_id ? (
                  <span className="text-sm">{t('admin.winnerSelected', 'Selected')}</span>
                ) : (
                  <span className="text-sm text-muted-foreground">{t('admin.pending', 'Pending')}</span>
                )}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end space-x-2">
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => fetchEntries(item.id)}
                    title={t('admin.viewEntries', 'View Entries')}
                  >
                    <Users className="h-4 w-4" />
                  </Button>
                  
                  {item.status === 'scheduled' && (
                    <>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleEdit(item)}
                        title={t('admin.edit', 'Edit')}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => runManualDraw(item.id)}
                        title={t('admin.runDraw', 'Run Draw')}
                      >
                        <Award className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleDelete(item.id)}
                    title={t('admin.delete', 'Delete')}
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default SweepstakesTable;
